import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {$createTextNode, $getSelection, $isRangeSelection} from 'lexical';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import EmojiPickerReact, {EmojiClickData, EmojiStyle, Theme} from 'emoji-picker-react';
import {createPortal} from 'react-dom';
import {noop} from '@priority-software/priority-style-react';
import {IconWithPopover} from '../IconWithPopover';
import {createDataTestId} from '../../../../../utils/dataTestId';
import {texts} from './texts';
import {isRTL} from '../../../../../Localization';
import {getCategories} from './utils';

interface IEmojiPickerProps {
    portalSelector?: string;
    content?: string;
    disabled?: boolean;
}

const rtl = isRTL();

export function EmojiPicker({portalSelector, content, disabled}: IEmojiPickerProps) {
    const [editor] = useLexicalComposerContext();
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);

    const buttonRef = useRef<HTMLButtonElement>(null);
    const pickerRef = useRef<HTMLDivElement>(null);

    const customCategories = getCategories();

    const handleEmojiClick = useCallback(
        (emojiData: EmojiClickData) => {
            editor?.update(() => {
                const selection = $getSelection();

                if (!$isRangeSelection(selection)) return;

                selection.insertNodes([$createTextNode(emojiData.emoji)]);
            });

            setShowEmojiPicker(false);
        },
        [editor]
    );

    // Position the emoji picker dropdown
    useEffect(() => {
        const button = buttonRef.current;
        const picker = pickerRef.current;

        if (showEmojiPicker && button && picker) {
            const {top, left} = button.getBoundingClientRect();
            const pickerHeight = picker.offsetHeight || 0;
            const windowHeight = window.innerHeight;
            const topPos = top + 40;

            if (topPos + pickerHeight > windowHeight) {
                picker.style.top = `${top - pickerHeight}px`;
            } else {
                picker.style.top = `${topPos}px`;
            }
            picker.style.left = `${Math.min(left, window.innerWidth - picker.offsetWidth - 20)}px`;
        }
    }, [showEmojiPicker]);

    // Handle outside clicks to close the picker
    useEffect(() => {
        const button = buttonRef.current;
        const picker = pickerRef.current;

        if (button && showEmojiPicker) {
            const handleClickOutside = (event: MouseEvent) => {
                const target = event.target as Node;
                if (!button.contains(target) && picker && !picker.contains(target)) {
                    setShowEmojiPicker(false);
                }
            };

            const handleKeyDown = (event: KeyboardEvent) => {
                if (event.key === 'Escape') {
                    event.preventDefault();
                    event.stopPropagation();
                    setShowEmojiPicker(false);
                }
            };

            document.addEventListener('click', handleClickOutside);
            window.addEventListener('keydown', handleKeyDown, true);

            return () => {
                document.removeEventListener('click', handleClickOutside);
                window.removeEventListener('keydown', handleKeyDown, true);
            };
        }

        return noop;
    }, [showEmojiPicker]);

    const renderButton = () => (
        <button
            {...createDataTestId('toolbar-emoji-picker-icon', false)}
            disabled={disabled}
            aria-label="Emoji picker"
            className="pui-emoji-picker"
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            ref={buttonRef}
        >
            <span className="text dropdown-button-text">🙂</span>
        </button>
    );

    const renderEmojiPicker = () => {
        if (!showEmojiPicker) return null;

        return createPortal(
            <div
                dir={rtl ? 'rtl' : 'ltr'}
                ref={pickerRef}
                className="pui-emoji__picker-container"
                {...createDataTestId('toolbar-emoji-picker-container', false)}
            >
                <EmojiPickerReact
                    skinTonesDisabled
                    onEmojiClick={handleEmojiClick}
                    theme={Theme.LIGHT}
                    emojiStyle={EmojiStyle.NATIVE}
                    height={350}
                    width={300}
                    searchDisabled={false}
                    searchPlaceHolder={texts.search_placeholder}
                    categories={customCategories}
                    previewConfig={{showPreview: false}}
                />
            </div>,
            (portalSelector && document.getElementById(portalSelector)) || document.body
        );
    };

    return (
        <>
            {content ? (
                <IconWithPopover type="children" content={content}>
                    {renderButton()}
                </IconWithPopover>
            ) : (
                renderButton()
            )}
            {renderEmojiPicker()}
        </>
    );
}
